import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Box,
    Typography,
    Button,
    Chip,
    Alert,
    CircularProgress,
    IconButton,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Autocomplete,
    Avatar,
} from '@mui/material';
import {
    ArrowBack,
    Add,
    CheckCircle,
    Cancel,
    Refresh,
    PowerOff,
    PowerSettingsNew,
    History,
    Edit,
    Autorenew,
    ExpandMore,
    ExpandLess,

} from '@mui/icons-material';
import { WarehouseAPI } from '../../services/api';
import { useToast } from '../common/useToast';
import { usePermission } from '../../hooks/usePermission';
import { ImageThumb, ImageLightbox, LightboxSection } from '../carding/CardingImageComponents';
import PageHeader from '../common/PageHeader';
import ListRowCard from '../common/ListRowCard';
import { eyebrow } from '../actionables/brand';
import { tokens } from '../../theme/tokens';
import { docColorBundle } from '../../utils/docStatus';
import { BundleCogs, formatAsOfMonth, missingSkusLabel } from '../../utils/cogsFormat';
import { AnimatedInr } from '../common/CogsVisuals';

// Semantic health hex — there is deliberately no green/amber/red token (tokens.ts is the warm
// brand palette only), so the DOC scale maps its MUI colour name onto these literals. `success`
// is the ONE unified green used across the whole detail view (lifecycle + DOC).
const DOC_HEX: Record<'success' | 'warning' | 'error' | 'default', string> = {
    success: '#15803D',
    warning: '#b45309',
    error: '#dc2626',
    default: tokens.muted,
};
const docHex = (doc: number | null | undefined) => DOC_HEX[docColorBundle(doc)];

const formatDoc = (doc: number | null | undefined) =>
    doc === null || doc === undefined || !isFinite(doc) ? '—' : doc.toFixed(1);

// Version COGS cell: a first-class member of the DetailRow metadata grid rather than a separate
// headline. It shares the exact DetailRow rhythm — the same 0.6rem uppercase #8A8078 eyebrow over
// the value, py 0.5 — so it aligns to the same columns and baseline as its siblings. The number is
// the emphasised figure (earns it through weight/size: ~1.1rem / 800 / ink mono + tabular-nums,
// counting up via AnimatedInr which honours reduced-motion), NOT through a loud colour. The
// partial 'N/M costed' qualifier and the as-of / earliest-available label sit as a quiet muted
// sub-line under the value, keeping the cell reading eyebrow → value → tiny qualifier.
const VersionCogsCell: React.FC<{ cogs?: BundleCogs }> = ({ cogs }) => {
    if (!cogs) return null;
    const asOfMonth = formatAsOfMonth(cogs.asOf);
    const isEarliest = cogs.costBasis === 'post_gst_snapshot_earliest';
    const showAsOf = cogs.frozen === true || !!asOfMonth || isEarliest;
    const asOfLabel = isEarliest
        ? `as of ${asOfMonth || 'Jun 2026'} · earliest available`
        : asOfMonth ? `as of ${asOfMonth}` : 'frozen';
    const hasTotal = cogs.total !== null;
    const showQualifier = hasTotal && ((cogs.partial) || showAsOf);
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, py: 0.5 }}>
            <Typography sx={{ ...eyebrow, color: '#8A8078', fontSize: '0.6rem' }}>
                COGS (post-GST)
            </Typography>
            {hasTotal ? (
                <AnimatedInr
                    value={cogs.total}
                    sx={{ fontFamily: 'monospace', fontWeight: 800, fontSize: '1.1rem', lineHeight: 1.1, color: tokens.ink, letterSpacing: '-0.01em' }}
                />
            ) : (
                <Tooltip title={cogs.missingSkus.length > 0 ? `No COGS found for: ${missingSkusLabel(cogs.missingSkus)}` : 'No COGS found'}>
                    <Typography component="span" sx={{ fontSize: '0.8rem', fontWeight: 700, color: tokens.muted, cursor: 'help' }}>
                        No cost data
                    </Typography>
                </Tooltip>
            )}
            {showQualifier && (
                <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.6 }}>
                    {cogs.partial && (
                        <Tooltip title={`${cogs.costedCount} of ${cogs.componentCount} components costed. Missing: ${missingSkusLabel(cogs.missingSkus)}`}>
                            <Typography component="span" sx={{ fontSize: '0.58rem', fontWeight: 700, color: tokens.muted, cursor: 'help', fontVariantNumeric: 'tabular-nums' }}>
                                {cogs.costedCount}/{cogs.componentCount} costed
                            </Typography>
                        </Tooltip>
                    )}
                    {showAsOf && (
                        <Tooltip title={isEarliest ? 'Cost as of the earliest available snapshot (Jun 2026) — this version predates COGS coverage.' : ''}>
                            <Typography component="span" sx={{ fontSize: '0.58rem', fontWeight: 600, color: tokens.muted, cursor: isEarliest ? 'help' : 'default' }}>
                                {asOfLabel}
                            </Typography>
                        </Tooltip>
                    )}
                </Box>
            )}
        </Box>
    );
};

interface BundleVersion {
    _id: string;
    needsReorder?: boolean;
    parentSKU: string;
    bundleSku: string;
    versionNumber: number;
    childs: string[];
    imageURL?: string;
    images?: string[];
    isActive: boolean;
    isFuture: boolean;
    createdAt: string;
    disabledAt?: string;
    createdBy?: string;
    disabledBy?: string;
    categoryCode?: string;
    name?: string;
    type?: string;
    saleType?: string;
    saleTag?: string;
    maxRetailPrice?: number;
    basePrice?: number;
    costPrice?: number;
    brand?: string;
    category?: string;
    subCategory?: string;
    handle?: string;
    tags?: string[];
    plytix_id?: string;
    cogs?: BundleCogs;
    componentsList?: Array<{ sku: string; isPackaging: boolean; extra?: boolean; cogs?: number | null; cogsPct?: number | null }>;
}

interface PlytixSkuOption {
    sku: string;
    name: string;
    category: string | null;
    handle: string | null;
    tags: string[];
    costPrice: number | null;
    basePrice: number | null;
    maxRetailPrice: number | null;
    plytixId: string | null;
    images: string[];
}

interface BundleCategoryDetailProps {
    parentSKU: string;
    onClose: () => void;
    onUpdate: () => void;
}

/* ── Mini stat chip ───────────────────────────────────────────────────────── */
const StatChip: React.FC<{
    label: string;
    value: number | string;
    color?: string;
}> = ({ label, value, color = tokens.muted }) => (
    <Chip
        size="small"
        label={
            <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                <Typography
                    component="span"
                    sx={{ fontSize: '0.65rem', fontWeight: 500, opacity: 0.75 }}
                >
                    {label}:
                </Typography>
                <Typography component="span" sx={{ fontSize: '0.7rem', fontWeight: 700 }}>
                    {value}
                </Typography>
            </Box>
        }
        variant="outlined"
        sx={{
            height: 22,
            borderColor: color,
            color,
            bgcolor: `${color}0f`,
            '& .MuiChip-label': { px: 0.8 },
        }}
    />
);

/* ── DOC chip — colored by the SAME semantic scale as the Simple/Bundle table rows
     (utils/docStatus docColorBundle → DOC_HEX), so the same SKU reads identically in
     both the table and the detail view. ── */
const DocChip: React.FC<{ label: string; doc: number | null | undefined }> = ({ label, doc }) => {
    const color = docHex(doc);
    return (
        <Chip
            size="small"
            label={
                <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                    <Typography component="span" sx={{ fontSize: '0.65rem', fontWeight: 500, opacity: 0.75 }}>
                        {label}:
                    </Typography>
                    <Typography component="span" sx={{ fontSize: '0.7rem', fontWeight: 700 }}>
                        {formatDoc(doc)}
                    </Typography>
                </Box>
            }
            variant="outlined"
            sx={{ height: 22, borderColor: color, color, bgcolor: `${color}14`, '& .MuiChip-label': { px: 0.8 } }}
        />
    );
};

/* ── Labeled spec row — label OVER value, left-aligned, so a grid cell reads as a clear
     eyebrow + value pair instead of a cramped space-between line. ── */
const DetailRow: React.FC<{
    label: string;
    value?: React.ReactNode;
    mono?: boolean;
}> = ({ label, value, mono }) => {
    if (value === undefined || value === null || value === '') return null;
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, py: 0.5 }}>
            <Typography sx={{ ...eyebrow, color: '#8A8078', fontSize: '0.6rem' }}>
                {label}
            </Typography>
            <Typography
                sx={{ fontSize: '0.8rem', fontWeight: 700, color: tokens.ink, fontFamily: mono ? 'monospace' : undefined }}
                noWrap
            >
                {value}
            </Typography>
        </Box>
    );
};

/* ── Main component ───────────────────────────────────────────────────────── */
const BundleCategoryDetail: React.FC<BundleCategoryDetailProps> = ({ parentSKU, onClose, onUpdate }) => {
    // RBAC: edit affordances on bundle versions are driven by the bundle-skus matrix
    // capability (replaces the old inline super-admin/admin role string check). The backend
    // independently enforces the same guard (defense in depth, SI-5).
    const canEditBundle = usePermission('bundle-skus').can('edit');
    // Creating a new bundle version is gated on the add-bundle capability (not
    // bundle-skus), so catalog roles that hold add-bundle:edit can create versions. The
    // backend enforces add-bundle:edit on POST /api/bundle-categories/:parentSKU/new-version.
    const canAddBundle = usePermission('add-bundle').can('edit');

    const [versions, setVersions] = useState<BundleVersion[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [actionLoading, setActionLoading] = useState<string | null>(null);
    const [reorderLoading, setReorderLoading] = useState<string | null>(null);
    // Historical (non-active) versions collapse to a slim row by default — the active
    // version is what people actually need to see, and repeating the full card for
    // every past version (often identical except for child SKUs) wastes space and
    // buries the one thing that matters. Expanded on click.
    const [expandedVersionIds, setExpandedVersionIds] = useState<Set<string>>(new Set());
    const [showNewVersionDialog, setShowNewVersionDialog] = useState(false);
    const [isFutureMode, setIsFutureMode] = useState(false);
    // Child SKUs — live Plytix search picker (replaces free-text entry, which accepted
    // any typo or nonexistent SKU with no validation), same pattern as CreateBundleCategory.
    const [newComponents, setNewComponents] = useState<PlytixSkuOption[]>([]);
    const [skuQuery, setSkuQuery] = useState('');
    const [skuOptions, setSkuOptions] = useState<PlytixSkuOption[]>([]);
    const [skuSearchLoading, setSkuSearchLoading] = useState(false);
    const skuSearchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const newChilds = newComponents.map(c => c.sku);
    const [newPlytixId, setNewPlytixId] = useState('');
    // Brand/Category/Tags are mandatory on every new version (including future versions)
    const [newBrand, setNewBrand] = useState('');
    const [newCategory, setNewCategory] = useState('');
    const [newTags, setNewTags] = useState<string[]>([]);
    const [brandOptions, setBrandOptions] = useState<string[]>([]);
    const [categoryOptions, setCategoryOptions] = useState<string[]>([]);
    const [tagOptions, setTagOptions] = useState<string[]>([]);
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [lightboxSections, setLightboxSections] = useState<LightboxSection[]>([]);
    const toast = useToast();

    // Edit version state
    const [editingVersion, setEditingVersion] = useState<BundleVersion | null>(null);
    const [editVersionPlytixId, setEditVersionPlytixId] = useState('');
    const [editVersionName, setEditVersionName] = useState('');

    // Full bundle edit state
    const [editBundleDialog, setEditBundleDialog] = useState<{
        open: boolean;
        version: BundleVersion | null;
        originalBundleSku: string;
    }>({ open: false, version: null, originalBundleSku: '' });
    const [bundleSkuConfirmText, setBundleSkuConfirmText] = useState('');
    const [componentsInput, setComponentsInput] = useState('');
    // Inventory state
    const [inventoryMap, setInventoryMap] = useState<Record<string, number>>({});
    // Can be made state
    const [canBeMadeMap, setCanBeMadeMap] = useState<Record<string, number>>({});
    // Per-version DRR / server-computed DOC, keyed by bundleSku. The versions endpoint carries no
    // drr/physicalDoc/potentialDoc, so DOC is sourced from the SAME expandVersions response the
    // Simple/Bundle table rows use — guaranteeing a SKU reads identically in both places.
    const [docMap, setDocMap] = useState<Record<string, { drr: number | null; physicalDoc: number | null; potentialDoc: number | null }>>({});

    const fetchInventory = useCallback(async (versionList: BundleVersion[]) => {
        const inventoryData: Record<string, number> = {};
        await Promise.all(
            versionList.map(async (v) => {
                try {
                    const result = await WarehouseAPI.getLocalBundleSKUInventory(v.bundleSku);
                    inventoryData[v.bundleSku] = result.total;
                } catch (err) {
                    console.error(`Failed to fetch inventory for ${v.bundleSku}:`, err);
                    inventoryData[v.bundleSku] = 0;
                }
            })
        );
        setInventoryMap((prev) => ({ ...prev, ...inventoryData }));
    }, []);

    const fetchCanBeMade = useCallback(async (versionList: BundleVersion[]) => {
        const canBeMadeData: Record<string, number> = {};
        await Promise.all(
            versionList.map(async (v) => {
                try {
                    const childSKUs = v.childs.map(skuId => ({ skuId, requiredQuantity: 1 }));
                    const result = await WarehouseAPI.calculateLocalCanBeMade(childSKUs);
                    canBeMadeData[v.bundleSku] = result.canBeMade;
                } catch (err) {
                    console.error(`Failed to calculate can-be-made for ${v.bundleSku}:`, err);
                    canBeMadeData[v.bundleSku] = 0;
                }
            })
        );
        setCanBeMadeMap((prev) => ({ ...prev, ...canBeMadeData }));
    }, []);

    // Per-version DRR + server DOC from the SAME source the Simple/Bundle table reads
    // (getBundleCategories with expandVersions). Fire-and-forget, errors swallowed — DOC chips
    // degrade to the null/outlined "—" state if this fails, exactly like the table.
    const fetchDoc = useCallback(async () => {
        try {
            const response = await WarehouseAPI.getBundleCategories(0, 100, parentSKU, 'name-asc', { expandVersions: 'true' });
            const map: Record<string, { drr: number | null; physicalDoc: number | null; potentialDoc: number | null }> = {};
            (response.data || []).forEach((row) => {
                const b = row.activeBundle;
                if (b && b.bundleSku) {
                    map[b.bundleSku] = {
                        drr: b.drr ?? null,
                        physicalDoc: b.physicalDoc ?? null,
                        potentialDoc: b.potentialDoc ?? null,
                    };
                }
            });
            setDocMap(map);
        } catch (err) {
            console.error(`Failed to fetch DOC data for ${parentSKU}:`, err);
        }
    }, [parentSKU]);

    const fetchVersions = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await WarehouseAPI.getBundleCategoryVersions(parentSKU);
            setVersions(data);
            if (data && data.length > 0) {
                fetchInventory(data);
                fetchCanBeMade(data);
                fetchDoc();
            }
        } catch (err: any) {
            setError(err.message || 'Failed to fetch versions');
        } finally {
            setLoading(false);
        }
    }, [parentSKU, fetchInventory, fetchCanBeMade, fetchDoc]);

    useEffect(() => {
        fetchVersions();
    }, [fetchVersions]);

    useEffect(() => {
        WarehouseAPI.getBundleCategoryFilterValues().then((vals) => {
            setBrandOptions(vals.brands || []);
            setTagOptions(vals.tags || []);
        }).catch(() => {});
        // Category is Plytix-sourced (live catalogue taxonomy), same as the main
        // Create Bundle dialog — not this tool's own (looser) category field.
        WarehouseAPI.getComponentCategoryOptions().then(setCategoryOptions).catch(() => {});
    }, []);

    // Debounced live SKU search against Redshift's plytix_catalogue — same pattern as
    // the main Create Bundle dialog's component picker.
    useEffect(() => {
        if (skuSearchDebounceRef.current) clearTimeout(skuSearchDebounceRef.current);
        if (!skuQuery.trim()) {
            setSkuOptions([]);
            return;
        }
        skuSearchDebounceRef.current = setTimeout(async () => {
            setSkuSearchLoading(true);
            try {
                const results = await WarehouseAPI.searchPlytixSkus(skuQuery.trim());
                setSkuOptions(results);
            } finally {
                setSkuSearchLoading(false);
            }
        }, 500);
        return () => { if (skuSearchDebounceRef.current) clearTimeout(skuSearchDebounceRef.current); };
    }, [skuQuery]);

    const activeBundle = versions.find((v) => v.isActive);

    const handleDisable = async (id: string) => {
        setActionLoading(id);
        try {
            const response = await WarehouseAPI.disableBundleCategory(id);
            await fetchVersions();
            onUpdate();
            if (response.plytixWarning) {
                toast.warning(response.message || 'Bundle disabled, but Plytix deactivation failed', { duration: 4000 });
            } else {
                toast.success(response.message || 'Bundle disabled successfully', { duration: 4000 });
            }
        } catch (err: any) {
            toast.error(err.message || 'Failed to disable bundle', { duration: 4000 });
        } finally {
            setActionLoading(null);
        }
    };

    const handleEnable = async (id: string) => {
        setActionLoading(id);
        try {
            const response = await WarehouseAPI.enableBundleCategory(id);
            await fetchVersions();
            onUpdate();
            if (response.plytixWarning) {
                toast.warning(response.message || 'Bundle enabled, but Plytix activation failed', { duration: 4000 });
            } else {
                toast.success(response.message || 'Bundle enabled successfully', { duration: 4000 });
            }
        } catch (err: any) {
            toast.error(err.message || 'Failed to enable bundle', { duration: 4000 });
        } finally {
            setActionLoading(null);
        }
    };

    const handleEditVersion = (version: BundleVersion) => {
        setEditingVersion(version);
        setEditVersionPlytixId(version.plytix_id || '');
        setEditVersionName(version.name || '');
    };

    const handleToggleReorder = async (versionId: string) => {
        setReorderLoading(versionId);
        try {
            const result = await WarehouseAPI.toggleBundleReorder(versionId);
            setVersions(prev => prev.map(v =>
                v._id === versionId ? { ...v, needsReorder: result.needsReorder } : v
            ));
        } catch (err) {
            toast.error((err instanceof Error ? err.message : 'Failed to toggle reorder status'), { duration: 4000 });
        } finally {
            setReorderLoading(null);
        }
    };

    const handleSaveVersionEdit = async () => {
        if (!editingVersion) return;
        setActionLoading(editingVersion._id);
        try {
            await WarehouseAPI.updateBundleVersionImage(
                editingVersion._id,
                undefined,
                editVersionPlytixId.trim() || undefined,
                editVersionName.trim()
            );
            await fetchVersions();
            onUpdate();
            toast.success('Version updated successfully', { duration: 4000 });
            setEditingVersion(null);
        } catch (err: any) {
            toast.error(err.message || 'Failed to update version', { duration: 4000 });
        } finally {
            setActionLoading(null);
        }
    };

    // Lightweight stand-in for a SKU that wasn't picked via live search this session
    // (e.g. prefilled from the previous version) — has enough shape to render as a chip.
    const skuStub = (sku: string): PlytixSkuOption => ({
        sku, name: sku, category: null, handle: null, tags: [],
        costPrice: null, basePrice: null, maxRetailPrice: null, plytixId: null, images: [],
    });

    const handleCreateNewVersion = async () => {
        if (newChilds.length === 0) {
            toast.error('At least one child SKU is required', { duration: 4000 });
            return;
        }
        if (!newBrand.trim()) {
            toast.error('Brand is required', { duration: 4000 });
            return;
        }
        if (!newCategory.trim()) {
            toast.error('Category is required', { duration: 4000 });
            return;
        }
        if (newTags.length === 0) {
            toast.error('At least one tag is required', { duration: 4000 });
            return;
        }
        setActionLoading('new-version');
        try {
            const payload = {
                childs: newChilds,
                isFuture: isFutureMode,
                plytix_id: newPlytixId.trim() || undefined,
                brand: newBrand.trim(),
                category: newCategory.trim(),
                tags: newTags,
            };
            await WarehouseAPI.createNewBundleVersion(parentSKU, payload);
            setShowNewVersionDialog(false);
            setNewComponents([]);
            setSkuQuery('');
            setSkuOptions([]);
            setNewPlytixId('');
            setNewBrand('');
            setNewCategory('');
            setNewTags([]);
            await fetchVersions();
            onUpdate();
            toast.success('New version created successfully', { duration: 4000 });
        } catch (err: any) {
            toast.error(err.message || 'Failed to create new version', { duration: 4000 });
        } finally {
            setActionLoading(null);
        }
    };

    const formatDateShort = (dateString: string) =>
        new Date(dateString).toLocaleDateString('en-IN', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        });

    const formatTime = (dateString: string) =>
        new Date(dateString).toLocaleTimeString('en-IN', {
            hour: '2-digit',
            minute: '2-digit',
        });

    const openNewVersionDialog = (future: boolean = false, preloadedChilds?: string[], preloadedPlytixId?: string) => {
        setIsFutureMode(future);
        setSkuQuery('');
        setSkuOptions([]);
        if (preloadedChilds) {
            setNewComponents(preloadedChilds.map(skuStub));
            setNewPlytixId(preloadedPlytixId || '');
        } else if (versions.length > 0) {
            const lastVersion = versions[0];
            setNewComponents((lastVersion.childs || []).map(skuStub));
            setNewPlytixId(lastVersion.plytix_id || '');
        } else {
            setNewComponents([]);
            setNewPlytixId('');
        }
        // Brand/Category/Tags default to the most recent version's values (still editable,
        // and still required) rather than being tied to which childs branch ran above.
        const lastVersion = versions[0];
        setNewBrand(lastVersion?.brand || '');
        setNewCategory(lastVersion?.category || '');
        setNewTags(lastVersion?.tags || []);
        setShowNewVersionDialog(true);
    };

    const toggleExpanded = (versionId: string) => {
        setExpandedVersionIds(prev => {
            const next = new Set(prev);
            if (next.has(versionId)) next.delete(versionId); else next.add(versionId);
            return next;
        });
    };

    // Primary/Non-Primary toggle chip — identical for active and disabled versions, and
    // also used in the collapsed row, so it lives once here instead of three times.
    const ReorderChip: React.FC<{ version: BundleVersion }> = ({ version }) => (
        <Tooltip
            title={
                !canEditBundle
                    ? 'Only Admin or Super Admin can change the Primary tag'
                    : version.needsReorder
                    ? 'Marked as Primary. Click to clear.'
                    : 'Click to mark this bundle as Primary'
            }
        >
          <span>
            <Chip
                size="small"
                icon={
                    reorderLoading === version._id ? (
                        <CircularProgress size={12} sx={{ color: version.needsReorder ? DOC_HEX.success : tokens.muted }} />
                    ) : (
                        <Autorenew
                            sx={{
                                fontSize: 13,
                                color: version.needsReorder ? DOC_HEX.success : tokens.muted,
                                animation: version.needsReorder ? 'spin 2.5s linear infinite' : 'none',
                                '@keyframes spin': {
                                    '0%': { transform: 'rotate(0deg)' },
                                    '100%': { transform: 'rotate(360deg)' },
                                },
                            }}
                        />
                    )
                }
                label={version.needsReorder ? 'Primary' : 'Non Primary'}
                variant={version.needsReorder ? 'filled' : 'outlined'}
                onClick={(e) => { e.stopPropagation(); canEditBundle && handleToggleReorder(version._id); }}
                disabled={reorderLoading === version._id || !canEditBundle}
                sx={{
                    height: 22,
                    fontWeight: 600,
                    fontSize: '0.7rem',
                    ...(version.needsReorder
                        ? {
                            color: DOC_HEX.success,
                            bgcolor: `${DOC_HEX.success}1a`,
                            '& .MuiChip-icon': { color: DOC_HEX.success, ml: 0.6 },
                            '&:hover': { bgcolor: `${DOC_HEX.success}1a` },
                        }
                        : {
                            borderColor: tokens.border,
                            color: tokens.muted,
                            bgcolor: '#FAFAF7',
                            '&:hover': { borderColor: DOC_HEX.success, color: DOC_HEX.success, bgcolor: `${DOC_HEX.success}1a` },
                        }),
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    '&.Mui-disabled': { opacity: 0.6 },
                }}
            />
          </span>
        </Tooltip>
    );

    return (
        <Box>
            {/* ── Muted "Back to Bundle Catalog" — above the shared PageHeader (Q1a); not orange,
                 not a fill. Does NOT touch PageHeader's own slots. ── */}
            <Button
                onClick={onClose}
                variant="text"
                startIcon={<ArrowBack sx={{ fontSize: 18 }} />}
                sx={{
                    color: tokens.muted,
                    textTransform: 'none',
                    fontWeight: 600,
                    minHeight: 'auto',
                    px: 0.5,
                    mb: 1,
                    '&:hover': { color: tokens.ink, bgcolor: 'transparent' },
                }}
            >
                Back to Bundle Catalog
            </Button>

            {/* ── Page header (shared PageHeader): ink title, eyebrow, subtitle, actions ── */}
            <PageHeader
                eyebrow="Bundle Catalog"
                title={parentSKU}
                subtitle={
                    <>
                        {versions.length} version{versions.length !== 1 ? 's' : ''}
                        {activeBundle ? (
                            <>
                                {' '}· Active: <Box component="span" sx={{ fontFamily: 'monospace' }}>
                                    {activeBundle.bundleSku}
                                </Box>
                            </>
                        ) : (
                            ' · No active bundle'
                        )}
                    </>
                }
                actions={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Tooltip title="Refresh data">
                            <IconButton
                                onClick={fetchVersions}
                                sx={{ color: tokens.muted, transition: 'transform 0.3s', '&:active': { transform: 'rotate(180deg)' } }}
                            >
                                <Refresh />
                            </IconButton>
                        </Tooltip>
                        {canAddBundle && (
                            <>
                                <Button
                                    variant="outlined"
                                    startIcon={<Add />}
                                    onClick={() => openNewVersionDialog(true)}
                                    sx={{
                                        py: 1,
                                        px: 2.5,
                                        minHeight: 38,
                                        fontWeight: 800,
                                        textTransform: 'none',
                                        borderRadius: '999px',
                                        borderColor: tokens.border,
                                        color: tokens.ink,
                                        '&:hover': { borderColor: '#C9C2B4', bgcolor: '#FAFAF7' },
                                    }}
                                >
                                    Future Version
                                </Button>
                                <Tooltip title={activeBundle ? `Disable ${activeBundle.bundleSku} first to create a new version` : ''}>
                                    <span>
                                        <Button
                                            variant="contained"
                                            disableElevation
                                            startIcon={<Add />}
                                            disabled={!!activeBundle}
                                            onClick={() => openNewVersionDialog(false)}
                                            sx={{
                                                py: 1,
                                                px: 2.5,
                                                minHeight: 38,
                                                fontWeight: 800,
                                                textTransform: 'none',
                                                borderRadius: '999px',
                                            }}
                                        >
                                            New Version
                                        </Button>
                                    </span>
                                </Tooltip>
                            </>
                        )}
                    </Box>
                }
            />

            {error && (
                <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
                    <CircularProgress size={40} sx={{ color: tokens.orange }} />
                </Box>
            )}

            {/* ── Version History ────────────────────────────────────────── */}
            {!loading && versions.length > 0 && (
                <Box>
                    {/* Section header */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                        <History sx={{ color: '#4A433D', fontSize: 20 }} />
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                            Version History
                        </Typography>
                    </Box>

                    {versions.map((version, versionIndex) => {
                        const inv = inventoryMap[version.bundleSku];
                        const canBeMade = canBeMadeMap[version.bundleSku];

                        // DOC derived identically to the Simple/Bundle table rows: prefer the server
                        // physicalDoc/potentialDoc, else fall back to inventory/drr and canBeMade/drr.
                        const d = docMap[version.bundleSku];
                        const physicalDoc = d?.physicalDoc ?? (d?.drr && d.drr > 0 && inv ? inv / d.drr : null);
                        const potentialDoc = d?.potentialDoc ?? (d?.drr && d.drr > 0 && canBeMade ? canBeMade / d.drr : null);

                        // Historical versions collapse by default (see expandedVersionIds above);
                        // the active version always renders in full.
                        const isExpanded = version.isActive || expandedVersionIds.has(version._id);

                        // What actually changed vs the next-older version — the real reason a new
                        // version usually exists. Surfaced as a small delta chip so the history
                        // reads like a changelog instead of N near-identical cards.
                        const olderVersion = versions[versionIndex + 1];
                        const addedChilds = olderVersion ? version.childs.filter(c => !olderVersion.childs.includes(c)) : [];
                        const removedChilds = olderVersion ? olderVersion.childs.filter(c => !version.childs.includes(c)) : [];
                        const addedSet = new Set(addedChilds);
                        const hasChildDiff = addedChilds.length > 0 || removedChilds.length > 0;
                        // Timeline + accent-strip state colour: active orange / future orange-dark /
                        // disabled muted-grey.
                        const stateColor = version.isActive ? tokens.orange : version.isFuture ? tokens.orangeDark : '#8A8078';
                        const dotColor = version.isActive ? tokens.orange : version.isFuture ? tokens.orangeDark : '#DAD6CC';

                        return (
                            <React.Fragment key={version._id}>
                            {/* Timeline rail */}
                            <Box sx={{ display: 'flex', gap: 1.5 }}>
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 12, pt: 2, flexShrink: 0 }}>
                                    <Box
                                        sx={{
                                            width: 10,
                                            height: 10,
                                            borderRadius: '50%',
                                            bgcolor: dotColor,
                                            flexShrink: 0,
                                            boxShadow: version.isActive ? '0 0 0 4px rgba(220, 88, 41, 0.15)' : 'none',
                                        }}
                                    />
                                    {versionIndex < versions.length - 1 && (
                                        <Box sx={{ width: '2px', flex: 1, bgcolor: tokens.border, mt: 0.5, minHeight: 20 }} />
                                    )}
                                </Box>

                                <Box sx={{ flex: 1, minWidth: 0, mb: 2 }}>
                                    {!isExpanded ? (
                                        /* ── Collapsed row ── */
                                        <Box
                                            onClick={() => toggleExpanded(version._id)}
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1.25,
                                                px: 1.5,
                                                py: 1,
                                                borderRadius: '10px',
                                                border: `1px solid ${tokens.border}`,
                                                bgcolor: tokens.surface,
                                                cursor: 'pointer',
                                                transition: 'border-color 150ms ease, background-color 150ms ease',
                                                '&:hover': { borderColor: '#B8B2A6', bgcolor: '#FAFAF7' },
                                            }}
                                        >
                                            <ImageThumb
                                                images={version.images || []}
                                                label={version.bundleSku}
                                                size={28}
                                                onClick={(e) => {
                                                    e?.stopPropagation();
                                                    if (version.images && version.images.length > 0) {
                                                        setLightboxSections([{ title: version.bundleSku, images: version.images }]);
                                                        setLightboxOpen(true);
                                                    }
                                                }}
                                            />
                                            <Typography sx={{ fontWeight: 700, fontSize: '0.82rem', flexShrink: 0 }} noWrap>
                                                {version.bundleSku}
                                            </Typography>
                                            <Chip
                                                size="small"
                                                label={version.isActive ? 'Active' : 'Disabled'}
                                                sx={{
                                                    height: 18, fontWeight: 600, fontSize: '0.6rem',
                                                    bgcolor: version.isActive ? `${DOC_HEX.success}1a` : '#F3F2EC',
                                                    color: version.isActive ? DOC_HEX.success : tokens.muted,
                                                    '& .MuiChip-label': { px: 0.6 },
                                                }}
                                            />
                                            {hasChildDiff && (
                                                <Chip
                                                    size="small"
                                                    label={`${addedChilds.length > 0 ? `+${addedChilds.length} ` : ''}${removedChilds.length > 0 ? `−${removedChilds.length}` : ''}`.trim()}
                                                    sx={{ height: 18, fontWeight: 700, fontSize: '0.6rem', bgcolor: 'rgba(220, 88, 41, 0.1)', color: tokens.orangeDark, '& .MuiChip-label': { px: 0.6 } }}
                                                />
                                            )}
                                            <Typography variant="caption" noWrap sx={{ fontSize: '0.7rem', color: tokens.muted }}>
                                                {version.childs.length} SKUs · {formatDateShort(version.createdAt)}
                                            </Typography>
                                            <Box sx={{ flex: 1 }} />
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>
                                                {!version.isActive && (
                                                    <Tooltip title={!!activeBundle ? 'Another bundle is already active' : 'Enable this bundle'}>
                                                        <span>
                                                            <IconButton
                                                                onClick={() => handleEnable(version._id)}
                                                                disabled={actionLoading === version._id || !!activeBundle}
                                                                size="small"
                                                                sx={{ p: 0.5, color: tokens.muted }}
                                                            >
                                                                {actionLoading === version._id ? <CircularProgress size={14} /> : <PowerSettingsNew sx={{ fontSize: 16 }} />}
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>
                                                )}
                                                <ExpandMore sx={{ fontSize: 18, color: tokens.muted }} />
                                            </Box>
                                        </Box>
                                    ) : (
                            <ListRowCard accent={stateColor} index={versionIndex}>
                                <Box sx={{ p: 2.5 }}>
                                    {/* ── Top row: thumbnail + info + actions ── */}
                                    <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                                        <ImageThumb
                                            images={version.images || []}
                                            label={version.bundleSku}
                                            onClick={(e) => {
                                                e?.stopPropagation();
                                                if (version.images && version.images.length > 0) {
                                                    setLightboxSections([{
                                                        title: version.bundleSku,
                                                        images: version.images
                                                    }]);
                                                    setLightboxOpen(true);
                                                }
                                            }}
                                            size={52}
                                        />

                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            {/* Title row */}
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
                                                <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1rem', lineHeight: 1.3 }}>
                                                    {version.bundleSku}
                                                </Typography>

                                                {version.isActive ? (
                                                    <>
                                                        <Chip
                                                            size="small"
                                                            icon={<CheckCircle sx={{ fontSize: 13 }} />}
                                                            label="Active"
                                                            color="success"
                                                            sx={{
                                                                height: 22,
                                                                fontWeight: 600,
                                                                fontSize: '0.7rem',
                                                                bgcolor: `${DOC_HEX.success}1a`,
                                                                color: DOC_HEX.success,
                                                                '& .MuiChip-icon': { fontSize: 13, ml: 0.6, color: DOC_HEX.success },
                                                            }}
                                                        />

                                                        <ReorderChip version={version} />
                                                    </>
                                                ) : (
                                                    <>
                                                        <Chip
                                                            size="small"
                                                            icon={<Cancel sx={{ fontSize: 13 }} />}
                                                            label="Disabled"
                                                            sx={{
                                                                height: 22,
                                                                fontWeight: 600,
                                                                fontSize: '0.7rem',
                                                                bgcolor: '#FAFAF7',
                                                                color: tokens.muted,
                                                                '& .MuiChip-icon': { fontSize: 13, ml: 0.6, color: tokens.muted },
                                                            }}
                                                        />
                                                        <ReorderChip version={version} />
                                                    </>
                                                )}
                                            </Box>

                                            {/* Sale type/tag, tags */}
                                            {(version.saleType || (version.tags && version.tags.length > 0)) && (
                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.4, mb: 0.75 }}>
                                                    {version.saleType && (
                                                        <Chip size="small" label={version.saleType} sx={{ height: 18, bgcolor: 'rgba(220, 88, 41, 0.06)', color: tokens.orangeDark, fontWeight: 600, fontSize: '0.6rem', '& .MuiChip-label': { px: 0.6 } }} />
                                                    )}
                                                    {version.tags?.map((tag, idx) => (
                                                        <Chip key={`${tag}-${idx}`} size="small" label={tag} sx={{ height: 18, bgcolor: '#FAFAF7', color: tokens.muted, fontWeight: 600, fontSize: '0.6rem', '& .MuiChip-label': { px: 0.6 } }} />
                                                    ))}
                                                </Box>
                                            )}

                                            {/* Timestamp */}
                                            <Typography
                                                variant="caption"
                                                sx={{ display: 'block', fontSize: '0.72rem', mb: 1, color: tokens.muted }}
                                            >
                                                Created: {formatDateShort(version.createdAt)}, {formatTime(version.createdAt)}
                                                {version.disabledAt && (
                                                    <>
                                                        {' '}· Disabled: {formatDateShort(version.disabledAt)},{' '}
                                                        {formatTime(version.disabledAt)}
                                                    </>
                                                )}
                                            </Typography>

                                            {/* Stat + DOC pills — DOC chips use the SAME semantic scale
                                                as the Simple/Bundle table; Inventory + Can be Made are
                                                neutral count chips. */}
                                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                                <DocChip label="Physical DOC" doc={physicalDoc} />
                                                <DocChip label="Potential DOC" doc={potentialDoc} />
                                                <StatChip label="Inventory" value={inv !== undefined ? inv : '…'} />
                                                {canBeMade !== undefined && (
                                                    <StatChip label="Can be Made" value={canBeMade} />
                                                )}
                                            </Box>
                                        </Box>

                                        {/* ── Action icons ── */}
                                        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'flex-start', flexShrink: 0 }}>
                                            {version.isActive ? (
                                                <Tooltip title="Disable this bundle">
                                                    <IconButton
                                                        onClick={() => handleDisable(version._id)}
                                                        disabled={actionLoading === version._id}
                                                        size="small"
                                                        sx={{
                                                            color: '#f59e0b',
                                                            transition: 'all 0.2s',
                                                            '&:hover': {
                                                                bgcolor: 'rgba(245, 158, 11, 0.12)',
                                                                transform: 'scale(1.1)',
                                                            },
                                                        }}
                                                    >
                                                        {actionLoading === version._id ? (
                                                            <CircularProgress size={18} sx={{ color: '#f59e0b' }} />
                                                        ) : (
                                                            <PowerOff fontSize="small" />
                                                        )}
                                                    </IconButton>
                                                </Tooltip>
                                            ) : (
                                                <>
                                                    <Tooltip
                                                        title={
                                                            !!activeBundle
                                                                ? 'Another bundle is already active'
                                                                : 'Enable this bundle'
                                                        }
                                                    >
                                                        <span>
                                                            <IconButton
                                                                onClick={() => handleEnable(version._id)}
                                                                disabled={actionLoading === version._id || !!activeBundle}
                                                                size="small"
                                                                sx={{
                                                                    color: tokens.muted,
                                                                    transition: 'all 0.2s',
                                                                    '&:hover': {
                                                                        bgcolor: 'rgba(148, 163, 184, 0.12)',
                                                                        transform: 'scale(1.1)',
                                                                    },
                                                                }}
                                                            >
                                                                {actionLoading === version._id ? (
                                                                    <CircularProgress size={18} sx={{ color: tokens.muted }} />
                                                                ) : (
                                                                    <PowerSettingsNew fontSize="small" />
                                                                )}
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>
                                                    {canEditBundle && (
                                                        <Tooltip title="Edit Name / Plytix ID">
                                                            <IconButton
                                                                onClick={() => handleEditVersion(version)}
                                                                disabled={actionLoading === version._id}
                                                                size="small"
                                                                sx={{
                                                                    color: tokens.orange,
                                                                    transition: 'all 0.2s',
                                                                    '&:hover': {
                                                                        bgcolor: 'rgba(220, 88, 41, 0.12)',
                                                                        transform: 'scale(1.1)',
                                                                    },
                                                                }}
                                                            >
                                                                <Edit fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                </>
                                            )}
                                            {canEditBundle && (
                                                <Tooltip title="Edit Bundle Details">
                                                    <IconButton
                                                        onClick={() => {
                                                            setEditBundleDialog({ open: true, version: { ...version, childs: [...version.childs] } as BundleVersion, originalBundleSku: version.bundleSku });
                                                            setBundleSkuConfirmText('');
                                                            setComponentsInput('');
                                                        }}
                                                        disabled={actionLoading === version._id}
                                                        size="small"
                                                        sx={{
                                                            color: tokens.orange,
                                                            transition: 'all 0.2s',
                                                            '&:hover': {
                                                                bgcolor: 'rgba(220, 88, 41, 0.12)',
                                                                transform: 'scale(1.1)',
                                                            },
                                                        }}
                                                    >
                                                        <Edit fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                            {!version.isActive && (
                                                <Tooltip title="Collapse">
                                                    <IconButton
                                                        onClick={() => toggleExpanded(version._id)}
                                                        size="small"
                                                        sx={{ color: tokens.muted, '&:hover': { color: tokens.ink, bgcolor: '#F3F2EC' } }}
                                                    >
                                                        <ExpandLess fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </Box>
                                    </Box>

                                    {/* Details panel — one responsive grid of label-over-value fields
                                        so the card fills its width and every value is unambiguous. */}
                                    <Box
                                        sx={{
                                            display: 'grid',
                                            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)', lg: 'repeat(4, 1fr)' },
                                            gap: 2,
                                            mb: 1.5,
                                        }}
                                    >
                                        <DetailRow label="Category Code" value={version.categoryCode} />
                                        <DetailRow label="Name" value={version.name} />
                                        <DetailRow label="Type" value={version.type} />
                                        <DetailRow label="Brand" value={version.brand} />
                                        <DetailRow label="Handle" value={version.handle} mono />
                                        <DetailRow label="Category" value={version.category} />
                                        <DetailRow label="Sub-Category" value={version.subCategory} />
                                        <DetailRow label="MRP" value={version.maxRetailPrice ? `₹${version.maxRetailPrice.toFixed(2)}` : undefined} />
                                        <DetailRow label="Base Price" value={version.basePrice ? `₹${version.basePrice.toFixed(2)}` : undefined} />
                                        {/* COGS (post-GST): version TOTAL only, as a uniform grid
                                            cell next to the money fields. The per-component cost
                                            breakdown lives ONLY in the Simple/Bundle Detailed view,
                                            never in Version History. */}
                                        <VersionCogsCell cogs={version.cogs} />
                                        <DetailRow label="Plytix ID" value={version.plytix_id} mono />
                                    </Box>

                                    {/* ── Child SKUs ── */}
                                    <Box>
                                        <Typography variant="caption" sx={{ mb: 0.5, display: 'block', fontSize: '0.7rem', color: tokens.muted }}>
                                            {version.childs.length} child SKUs
                                        </Typography>
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.4 }}>
                                            {version.childs.map((child) => (
                                                <Chip
                                                    key={child}
                                                    label={child}
                                                    size="small"
                                                    variant="outlined"
                                                    sx={{
                                                        height: 18, fontSize: '0.62rem', fontFamily: 'monospace', '& .MuiChip-label': { px: 0.6 },
                                                        ...(addedSet.has(child) ? { borderColor: DOC_HEX.success, color: DOC_HEX.success, bgcolor: `${DOC_HEX.success}14` } : {}),
                                                    }}
                                                />
                                            ))}
                                        </Box>
                                        {removedChilds.length > 0 && (
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.4, mt: 0.5 }}>
                                                {removedChilds.map((child) => (
                                                    <Chip
                                                        key={child}
                                                        label={child}
                                                        size="small"
                                                        variant="outlined"
                                                        sx={{
                                                            height: 18, fontSize: '0.62rem', fontFamily: 'monospace', '& .MuiChip-label': { px: 0.6 },
                                                            borderColor: DOC_HEX.error, color: DOC_HEX.error, bgcolor: `${DOC_HEX.error}14`,
                                                            textDecoration: 'line-through',
                                                        }}
                                                    />
                                                ))}
                                            </Box>
                                        )}
                                    </Box>
                                </Box>
                            </ListRowCard>
                                    )}
                                </Box>
                            </Box>
                            </React.Fragment>
                        );
                    })}
                </Box>
            )}

            {/* ── New Version Dialog ── */}
            <Dialog open={showNewVersionDialog} onClose={() => setShowNewVersionDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontWeight: 700 }}>
                    {isFutureMode ? 'Create Future Version' : 'Create New Version'}
                </DialogTitle>
                <DialogContent dividers>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Creating {isFutureMode ? 'future' : 'new'} version:{' '}
                        <strong>{parentSKU}-{(versions[0]?.versionNumber || 0) + 1}</strong>
                    </Typography>
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                            Child SKUs *
                        </Typography>
                        <Autocomplete
                            multiple
                            options={skuOptions}
                            value={newComponents}
                            onChange={(_, val) => setNewComponents(val)}
                            onInputChange={(_, val) => setSkuQuery(val)}
                            getOptionLabel={(opt) => `${opt.sku} — ${opt.name}`}
                            isOptionEqualToValue={(opt, val) => opt.sku === val.sku}
                            loading={skuSearchLoading}
                            filterOptions={(x) => x}
                            renderOption={(props, option) => (
                                <Box component="li" {...props} key={option.sku} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                    <Avatar src={option.images[0]} variant="rounded" sx={{ width: 32, height: 32, bgcolor: 'grey.200' }}>
                                        {option.sku[0]}
                                    </Avatar>
                                    <Box>
                                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>{option.sku}</Typography>
                                        <Typography variant="caption" color="text.secondary">{option.name}</Typography>
                                    </Box>
                                </Box>
                            )}
                            renderTags={(value, getTagProps) =>
                                value.map((opt, index) => (
                                    <Chip
                                        {...getTagProps({ index })}
                                        key={opt.sku}
                                        avatar={<Avatar src={opt.images[0]}>{opt.sku[0]}</Avatar>}
                                        label={opt.sku}
                                        size="small"
                                    />
                                ))
                            }
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    placeholder="Search by SKU and add each component..."
                                    helperText="Searches Redshift's live product catalogue by SKU prefix — pick real SKUs instead of typing them by hand"
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                {skuSearchLoading ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    }}
                                />
                            )}
                        />
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                        <Autocomplete
                            fullWidth
                            options={brandOptions}
                            value={newBrand || null}
                            onChange={(_, val) => setNewBrand(val || '')}
                            renderInput={(params) => (
                                <TextField {...params} label="Brand" placeholder="Pick a brand" required />
                            )}
                        />
                        <Autocomplete
                            fullWidth
                            options={categoryOptions}
                            value={newCategory || null}
                            onChange={(_, val) => setNewCategory(val || '')}
                            renderInput={(params) => (
                                <TextField {...params} label="Category" placeholder="Pick a category" required />
                            )}
                        />
                    </Box>
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                            Tags *
                        </Typography>
                        <Autocomplete
                            multiple
                            freeSolo
                            fullWidth
                            options={tagOptions}
                            value={newTags}
                            onChange={(_, val) => setNewTags(val as string[])}
                            renderTags={(value, getTagProps) =>
                                value.map((tag, index) => (
                                    <Chip
                                        {...getTagProps({ index })}
                                        key={`${tag}-${index}`}
                                        label={tag}
                                        size="small"
                                        color="secondary"
                                        variant="outlined"
                                    />
                                ))
                            }
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    placeholder={newTags.length === 0 ? 'Select or type a tag and press Enter' : ''}
                                    size="small"
                                    required={newTags.length === 0}
                                />
                            )}
                        />
                        {newTags.length === 0 && (
                            <Typography variant="caption" color="text.secondary">
                                At least one tag is required
                            </Typography>
                        )}
                    </Box>
                    <TextField
                        fullWidth
                        label="Plytix ID (Optional)"
                        value={newPlytixId}
                        onChange={(e) => setNewPlytixId(e.target.value)}
                        placeholder="Enter Plytix product ID"
                        helperText="Unique identifier from Plytix system"
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowNewVersionDialog(false)} sx={{ color: tokens.muted, textTransform: 'none' }}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleCreateNewVersion}
                        disabled={actionLoading === 'new-version' || newChilds.length === 0 || !newBrand.trim() || !newCategory.trim() || newTags.length === 0}
                        color={isFutureMode ? 'secondary' : 'primary'}
                        sx={{ borderRadius: '999px', textTransform: 'none', fontWeight: 800, minHeight: 38 }}
                    >
                        {actionLoading === 'new-version' ? (
                            <CircularProgress size={22} sx={{ color: 'white' }} />
                        ) : isFutureMode ? (
                            'Create Future Version'
                        ) : (
                            'Create Version'
                        )}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ── Edit Version Dialog ── */}
            <Dialog open={!!editingVersion} onClose={() => setEditingVersion(null)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontWeight: 700 }}>Edit Version</DialogTitle>
                <DialogContent dividers>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                            Editing: <strong>{editingVersion?.bundleSku}</strong>
                        </Typography>
                        <TextField
                            fullWidth
                            label="Name"
                            value={editVersionName}
                            onChange={(e) => setEditVersionName(e.target.value)}
                            placeholder="Enter a friendly name for this version"
                            helperText="Display name shown for this bundle version"
                        />
                        <TextField
                            fullWidth
                            label="Plytix ID"
                            value={editVersionPlytixId}
                            onChange={(e) => setEditVersionPlytixId(e.target.value)}
                            placeholder="Enter Plytix product ID"
                            helperText="Unique identifier from Plytix system"
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditingVersion(null)} disabled={actionLoading === editingVersion?._id} sx={{ color: tokens.muted, textTransform: 'none' }}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleSaveVersionEdit}
                        disabled={actionLoading === editingVersion?._id}
                        sx={{ borderRadius: '999px', textTransform: 'none', fontWeight: 800, minHeight: 38 }}
                    >
                        {actionLoading === editingVersion?._id ? <CircularProgress size={22} sx={{ color: 'white' }} /> : 'Save Changes'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ── Edit Bundle Dialog ── */}
            <Dialog open={editBundleDialog.open} onClose={() => setEditBundleDialog({ open: false, version: null, originalBundleSku: '' })} maxWidth="md" fullWidth>
                <DialogTitle sx={{ fontWeight: 700 }}>Edit Bundle: {editBundleDialog.originalBundleSku}</DialogTitle>
                <DialogContent dividers>
                    {editBundleDialog.version && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
                            {/* Bundle SKU — the actual identifier synced with Unicommerce/Shopify */}
                            <Box>
                                <TextField
                                    label="Bundle SKU"
                                    value={editBundleDialog.version.bundleSku || ''}
                                    onChange={(e) => setEditBundleDialog(prev => ({ ...prev, version: { ...prev.version!, bundleSku: e.target.value.toUpperCase() } }))}
                                    size="small"
                                    fullWidth
                                    sx={{ maxWidth: 320 }}
                                />
                                {editBundleDialog.version.bundleSku !== editBundleDialog.originalBundleSku && (
                                    <Box sx={{ mt: 1.5 }}>
                                        <Alert severity="warning" sx={{ mb: 1.5 }}>
                                            This is the actual SKU identifier matched by Unicommerce, Shopify, and inventory
                                            sync. Renaming it here does <strong>not</strong> rename it in those systems —
                                            you must rename it there too, or inventory/DRR figures for this bundle will
                                            stop matching. Type the new SKU below to confirm.
                                        </Alert>
                                        <TextField
                                            label={`Type "${editBundleDialog.version.bundleSku}" to confirm`}
                                            value={bundleSkuConfirmText}
                                            onChange={(e) => setBundleSkuConfirmText(e.target.value.toUpperCase())}
                                            size="small"
                                            fullWidth
                                            sx={{ maxWidth: 320 }}
                                            color="warning"
                                        />
                                    </Box>
                                )}
                            </Box>

                            {/* Bundle Details */}
                            <Box>
                                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>Bundle Details</Typography>
                                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 2 }}>
                                    <TextField label="Name" value={editBundleDialog.version.name || ''} onChange={(e) => setEditBundleDialog(prev => ({ ...prev, version: { ...prev.version!, name: e.target.value } }))} size="small" />
                                    <TextField label="Category Code" value={editBundleDialog.version.categoryCode || ''} onChange={(e) => setEditBundleDialog(prev => ({ ...prev, version: { ...prev.version!, categoryCode: e.target.value } }))} size="small" />
                                    <TextField label="Type" value={editBundleDialog.version.type || ''} onChange={(e) => setEditBundleDialog(prev => ({ ...prev, version: { ...prev.version!, type: e.target.value } }))} size="small" />
                                    <TextField label="Sale Type" select value={editBundleDialog.version.saleType || 'Bundle'} onChange={(e) => setEditBundleDialog(prev => ({ ...prev, version: { ...prev.version!, saleType: e.target.value } }))} size="small" SelectProps={{ native: true }}>
                                        <option value="Bundle">Bundle</option>
                                        <option value="Simple">Simple</option>
                                    </TextField>
                                </Box>
                            </Box>
                            
                            {/* Additional Information */}
                            <Box>
                                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>Additional Information</Typography>
                                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 2 }}>
                                    <TextField label="Brand" value={editBundleDialog.version.brand || ''} onChange={(e) => setEditBundleDialog(prev => ({ ...prev, version: { ...prev.version!, brand: e.target.value } }))} size="small" />
                                    <TextField label="Category" value={editBundleDialog.version.category || ''} onChange={(e) => setEditBundleDialog(prev => ({ ...prev, version: { ...prev.version!, category: e.target.value } }))} size="small" />
                                    <TextField label="Sub Category" value={editBundleDialog.version.subCategory || ''} onChange={(e) => setEditBundleDialog(prev => ({ ...prev, version: { ...prev.version!, subCategory: e.target.value } }))} size="small" />
                                    <TextField label="Handle" value={editBundleDialog.version.handle || ''} onChange={(e) => setEditBundleDialog(prev => ({ ...prev, version: { ...prev.version!, handle: e.target.value } }))} size="small" />
                                </Box>
                            </Box>

                            {/* Pricing */}
                            <Box>
                                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>Pricing</Typography>
                                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 2 }}>
                                    <TextField label="MRP" type="number" value={editBundleDialog.version.maxRetailPrice || ''} onChange={(e) => setEditBundleDialog(prev => ({ ...prev, version: { ...prev.version!, maxRetailPrice: Number(e.target.value) } }))} size="small" />
                                    <TextField label="Base Price" type="number" value={editBundleDialog.version.basePrice || ''} onChange={(e) => setEditBundleDialog(prev => ({ ...prev, version: { ...prev.version!, basePrice: Number(e.target.value) } }))} size="small" />
                                    <TextField label="Cost Price" type="number" value={editBundleDialog.version.costPrice || ''} onChange={(e) => setEditBundleDialog(prev => ({ ...prev, version: { ...prev.version!, costPrice: Number(e.target.value) } }))} size="small" />
                                </Box>
                            </Box>

                            {/* Child SKUs */}
                            <Box>
                                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>Child SKUs</Typography>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                                    {editBundleDialog.version.childs?.map((sku, index) => (
                                        <Chip
                                            key={index}
                                            label={sku}
                                            onDelete={() => {
                                                setEditBundleDialog(prev => ({
                                                    ...prev,
                                                    version: {
                                                        ...prev.version!,
                                                        childs: prev.version!.childs.filter((_, i) => i !== index)
                                                    }
                                                }));
                                            }}
                                            size="small"
                                        />
                                    ))}
                                </Box>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <TextField
                                        fullWidth
                                        placeholder="Enter child SKU and press Enter"
                                        value={componentsInput}
                                        onChange={(e) => setComponentsInput(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                e.preventDefault();
                                                if (componentsInput.trim()) {
                                                    setEditBundleDialog(prev => ({
                                                        ...prev,
                                                        version: {
                                                            ...prev.version!,
                                                            childs: [...(prev.version?.childs || []), componentsInput.trim().toUpperCase()]
                                                        }
                                                    }));
                                                    setComponentsInput('');
                                                }
                                            }
                                        }}
                                        size="small"
                                    />
                                    <Button
                                        variant="outlined"
                                        onClick={() => {
                                            if (componentsInput.trim()) {
                                                setEditBundleDialog(prev => ({
                                                    ...prev,
                                                    version: {
                                                        ...prev.version!,
                                                        childs: [...(prev.version?.childs || []), componentsInput.trim().toUpperCase()]
                                                    }
                                                }));
                                                setComponentsInput('');
                                            }
                                        }}
                                        sx={{ minWidth: 40 }}
                                    >
                                        <Add />
                                    </Button>
                                </Box>
                            </Box>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setEditBundleDialog({ open: false, version: null, originalBundleSku: '' })} sx={{ color: tokens.muted, textTransform: 'none' }}>Cancel</Button>
                    <Button
                        variant="contained"
                        sx={{ borderRadius: '999px', textTransform: 'none', fontWeight: 800, minHeight: 38 }}
                        disabled={!!actionLoading || (
                            editBundleDialog.version?.bundleSku !== editBundleDialog.originalBundleSku &&
                            bundleSkuConfirmText !== editBundleDialog.version?.bundleSku
                        )}
                        onClick={async () => {
                            if (!editBundleDialog.version) return;
                            setActionLoading('edit-bundle');
                            try {
                                await WarehouseAPI.updateBundleFullDetails(editBundleDialog.version._id, editBundleDialog.version);
                                toast.success('Bundle details updated successfully!', { duration: 4000 });
                                setEditBundleDialog({ open: false, version: null, originalBundleSku: '' });
                                fetchVersions();
                                onUpdate();
                            } catch (err: any) {
                                toast.error(err.message || 'Failed to update bundle details', { duration: 4000 });
                            } finally {
                                setActionLoading(null);
                            }
                        }}
                    >
                        {actionLoading === 'edit-bundle' ? <CircularProgress size={24} /> : 'Save Changes'}
                    </Button>
                </DialogActions>
            </Dialog>


            <ImageLightbox
                open={lightboxOpen}
                onClose={() => setLightboxOpen(false)}
                sections={lightboxSections}
            />
        </Box>
    );
};

export default BundleCategoryDetail;
